import type { Request } from '@/types/requests';

/**
 * Base template for mock Request data based on real request structure
 */
const getBaseRequestTemplate = (): Omit<Request, 'ta_orderrequestid'> => ({
	ta_name: 'Order Request',
	ta_orderrequestdate: '2025-01-15',
	ta_quantity: 1,
	ta_status: 953870000, // Pending Shipment status code
	ta_clinicpo: '',
	ta_dos: '2025-01-10',
	ta_comments: '',
	_ta_bvid_value: undefined, // Bulk order (no BV)
	ta_cliniccity: '',
	ta_clinicstate: '',
	ta_skudescription: '',
	ta_fedexshippingmethod: 0,
	createdon: '2025-01-15T10:00:00Z',
	statuscode: 1,
	statecode: 0,
	ta_SKU: {
		ta_productid: 'prod-001',
		ta_name: 'Axolotl Dual Graft (Q4332)',
		ta_size: '2x2',
	},
	ta_Clinic: {
		accountid: '61aea445-e3b4-ea11-a816-000d3a191041',
		name: 'A Podiatry Clinic',
	},
	ta_Doctor: {
		contactid: 'doc-001',
		fullname: 'Edward Jenner',
	},
	ta_BVPatient: undefined,
	ta_ShippingLocation: {
		ta_shippinglocationsid: 'ship-001',
		ta_name: 'Main Office',
		ta_addressline: '123 Main Street, New York, NY 10001',
		ta_citylocality: 'New York',
		ta_stateprovince: 'NY',
	},
	shipments: [],
});

/**
 * Creates mock Request data for the tutorial
 * @param index - The index of the mock Request to create (0-9)
 * @returns A mock Request object
 */
export const createMockRequest = (index: number): Request => {
	const baseDate = new Date('2025-01-15');
	const requestDate = new Date(baseDate);
	requestDate.setDate(requestDate.getDate() - index * 5);

	const dosDate = new Date(requestDate);
	dosDate.setDate(dosDate.getDate() - 5);

	// Product variations
	const products = [
		{ id: 'prod-001', name: 'Axolotl Dual Graft (Q4332)', size: '2x2' },
		{ id: 'prod-002', name: 'Amnio-Maxx (Q4239)', size: '3x3' },
		{ id: 'prod-003', name: 'Helicoll (Q4164)', size: '2x2' },
		{ id: 'prod-004', name: 'DermaMatrix (Q4123)', size: '4x4' },
		{ id: 'prod-005', name: 'AlloDerm (Q4056)', size: '2x2' },
	];

	// Clinic variations
	const clinics = [
		{ id: '61aea445-e3b4-ea11-a816-000d3a191041', name: 'A Podiatry Clinic' },
		{
			id: '72bfb556-f4c5-fb22-b927-000d3a191042',
			name: 'Podiatry Care Clinic',
		},
		{
			id: '83cgc667-g5d6-gc33-ca38-000d3a191043',
			name: 'Advanced Foot & Ankle',
		},
		{
			id: '94dhd778-h6e7-hd44-db49-000d3a191044',
			name: 'Coastal Podiatry Group',
		},
		{
			id: 'a5eie889-i7f8-ie55-ec5a-000d3a191045',
			name: 'Metro Foot Specialists',
		},
	];

	// Doctor variations
	const doctors = [
		{ id: 'doc-001', name: 'Edward Jenner' },
		{ id: 'doc-002', name: 'Dr. Sarah Johnson' },
		{ id: 'doc-003', name: 'Dr. Michael Chen' },
		{ id: 'doc-004', name: 'Dr. Emily Rodriguez' },
		{ id: 'doc-005', name: 'Dr. David Thompson' },
	];

	// Patient variations (for Individual orders)
	const patients = [
		{ id: 'pat-001', name: 'Andrew Simon' },
		{ id: 'pat-002', name: 'William Reed' },
		{ id: 'pat-003', name: 'Sarah Johnson' },
		{ id: 'pat-004', name: 'Robert Brown' },
		{ id: 'pat-005', name: 'Maria Garcia' },
	];

	// Shipping location variations
	const shippingLocations = [
		{
			id: 'ship-001',
			name: 'Main Office',
			address: '123 Main Street, New York, NY 10001',
			city: 'New York',
			state: 'NY',
		},
		{
			id: 'ship-002',
			name: 'Branch Office',
			address: '456 Oak Avenue, Los Angeles, CA 90001',
			city: 'Los Angeles',
			state: 'CA',
		},
		{
			id: 'ship-003',
			name: 'Warehouse',
			address: '789 Pine Road, Chicago, IL 60601',
			city: 'Chicago',
			state: 'IL',
		},
		{
			id: 'ship-004',
			name: 'Distribution Center',
			address: '321 Elm Street, Houston, TX 77001',
			city: 'Houston',
			state: 'TX',
		},
		{
			id: 'ship-005',
			name: 'Regional Office',
			address: '654 Maple Drive, Phoenix, AZ 85001',
			city: 'Phoenix',
			state: 'AZ',
		},
	];

	// Status variations
	const statuses = [
		953870000, // Pending Shipment
		953870001, // Shipped
		953870002, // Order Denied
		953870003, // Ready To Ship
		953870005, // Partial Shipment
	];
	const status = statuses[index % statuses.length];

	// Determine order type: mix of Individual (with BV) and Bulk (no BV)
	const isIndividual = index % 3 === 0; // Every 3rd order is Individual
	const bvId = isIndividual ? `bv-${String(1000 + index).padStart(4, '0')}` : undefined;

	const product = products[index % products.length];
	const clinic = clinics[index % clinics.length];
	const doctor = doctors[index % doctors.length];
	const patient = isIndividual ? patients[index % patients.length] : undefined;
	const shippingLocation = shippingLocations[index % shippingLocations.length];

	const orderId = crypto.randomUUID();
	const clinicPO = index % 2 === 0 ? `PO-${String(3000 + index).padStart(4, '0')}` : '';

	const baseTemplate = getBaseRequestTemplate();

	return {
		...baseTemplate,
		ta_orderrequestid: orderId,
		ta_name: `Order Request ${index + 1}`,
		ta_orderrequestdate: requestDate.toISOString().split('T')[0],
		ta_quantity: (index % 3) + 1, // 1, 2, or 3
		ta_status: status,
		ta_clinicpo: clinicPO,
		ta_dos: dosDate.toISOString().split('T')[0],
		_ta_bvid_value: bvId,
		createdon: requestDate.toISOString(),
		ta_SKU: {
			ta_productid: product.id,
			ta_name: product.name,
			ta_size: product.size,
		},
		ta_Clinic: {
			accountid: clinic.id,
			name: clinic.name,
		},
		ta_Doctor: {
			contactid: doctor.id,
			fullname: doctor.name,
		},
		ta_BVPatient: patient
			? {
					contactid: patient.id,
					fullname: patient.name,
				}
			: undefined,
		ta_ShippingLocation: {
			ta_shippinglocationsid: shippingLocation.id,
			ta_name: shippingLocation.name,
			ta_addressline: shippingLocation.address,
			ta_citylocality: shippingLocation.city,
			ta_stateprovince: shippingLocation.state,
		},
		// Add shipments for Shipped and Partial Shipment statuses
		shipments:
			status === 953870001 || status === 953870005
				? [
						{
							ta_inventoryid: `inv-${index}`,
							ta_tissueid: `TID-${String(1000 + index).padStart(4, '0')}`,
							ta_trackingnumber: `1Z999AA1012345678${index}`,
							ta_fedexshipmentstatus: 'Delivered',
							ta_deliverydate: new Date(requestDate.getTime() + 3 * 24 * 60 * 60 * 1000)
								.toISOString()
								.split('T')[0],
							createdon: new Date(requestDate.getTime() + 1 * 24 * 60 * 60 * 1000).toISOString(),
						},
					]
				: [],
	};
};

/**
 * Generates an array of mock Request data for the tutorial
 * @param count - Number of mock Requests to generate (default: 10)
 * @returns Array of mock Request objects
 */
export const generateMockRequestData = (count = 10): Request[] => {
	return Array.from({ length: count }, (_, index) => createMockRequest(index));
};
